
import './App.css';
import { useEffect, useState } from 'react';
import AuthForm from './components/AuthForm';
import Dashboard from './components/Dashboard';
import DeviceDetails from './components/DeviceDetails';
import TrackingPage from './components/TrackingPage';
import { hasSession, logout, setOnAuthLost } from './auth/session';
 
const pageRoutes = [
  { page: 'devices', path: '/devices' },
  { page: 'add', path: '/add-device' },
  { page: 'tripreport', path: '/tripreport' },
  { page: 'assigned-route', path: '/assign-route' },
  { page: 'relay-control', path: '/relay-control' },
  { page: 'geofencing', path: '/geofencing' },
  { page: 'alerts', path: '/alerts' },
  { page: 'device-monitor', path: '/device-monitor' },
  { page: 'maintenance', path: '/maintenance' },
  { page: 'documents', path: '/documents' },
  { page: 'support', path: '/support' },
];
 
const pageToPath = Object.fromEntries(pageRoutes.map(r => [r.page, r.path]));
pageToPath.dashboard = '/';
 
// Matches "/alerts" AND "/alerts/8801234567" -> 'alerts'
const resolvePageFromPath = (path) => {
  const match = pageRoutes.find(r => path === r.path || path.startsWith(r.path + '/'));
  return match ? match.page : 'dashboard';
};
 
function App() {
  const initPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const mdDetailsInit = initPath.match(/^\/device\/(\d{10,17})\/details$/);
  const mdTrackInit = initPath.match(/^\/device\/(\d{10,17})\/track$/);
 
  // "Logged in" = there is a refresh token. The access token is created on demand.
  const [authed, setAuthed] = useState(() => hasSession());
  const [detailImei, setDetailImei] = useState(() => (mdDetailsInit ? mdDetailsInit[1] : null));
  const [trackImei, setTrackImei] = useState(() => (mdTrackInit ? mdTrackInit[1] : null));
  const [dashPage, setDashPage] = useState(() => {
    if (mdDetailsInit) return 'dashboard';
    if (mdTrackInit) return 'dashboard';
    const resolved = resolvePageFromPath(initPath);
    return resolved !== 'dashboard' ? resolved : (localStorage.getItem('lastPage') || 'dashboard');
  });
 
  // Called by session.js when the session ends for real (refresh rejected, or
  // another tab logged out). Sends the user back to the login screen.
  useEffect(() => {
    setOnAuthLost(() => {
      setAuthed(false);
      setDetailImei(null);
      setTrackImei(null);
      window.history.pushState({}, '', '/');
    });
    return () => setOnAuthLost(null);
  }, []);
 
  // Handles browser Back/Forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const mdDetails = path.match(/^\/device\/(\d{10,17})\/details$/);
      const mdTrack = path.match(/^\/device\/(\d{10,17})\/track$/);
 
      if (mdDetails) {
        setDetailImei(mdDetails[1]);
        setTrackImei(null);
        return;
      }
      if (mdTrack) {
        setTrackImei(mdTrack[1]);
        setDetailImei(null);
        return;
      }
 
      setDetailImei(null);
      setTrackImei(null);
      setDashPage(resolvePageFromPath(path));
    };
 
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
 
  useEffect(() => {
    if (authed) {
      document.body.classList.add('dashboard');
      document.body.classList.remove('auth');
    } else {
      document.body.classList.add('auth');
      document.body.classList.remove('dashboard');
    }
  }, [authed]);
 
  if (authed && trackImei) {
    return <TrackingPage imei={trackImei} onBack={() => {
      setTrackImei(null);
      setDashPage('devices');
      window.history.pushState({}, '', '/devices');
    }} />;
  }
 
  if (authed && detailImei) {
    return <DeviceDetails imei={detailImei} onBack={() => { setDetailImei(null); window.history.pushState({}, '', '/'); }} />;
  }
 
  if (authed) {
    return <Dashboard
      initialPage={dashPage}
      onLogout={async () => {
        await logout(); // clears local tokens and revokes the session on the server
        setAuthed(false);
        window.history.pushState({}, '', '/');
      }}
      onPageChange={(page) => {
        setDashPage(page);
        localStorage.setItem('lastPage', page);
        window.history.pushState({}, '', pageToPath[page] || '/');
      }}
      onTrack={(imei, mode = 'details') => {
        if (mode === 'track') {
          setTrackImei(imei);
          window.history.pushState({}, '', `/device/${imei}/track`);
        } else {
          setDetailImei(imei);
          window.history.pushState({}, '', `/device/${imei}/details`);
        }
      }}
    />;
  }
 
  // AuthForm already saved the tokens before calling onLogin
  return <AuthForm onLogin={() => {
    setAuthed(true);
    setDetailImei(null);
    setTrackImei(null);
    setDashPage('dashboard');
    window.history.pushState({}, '', '/');
  }} />;
}
 
export default App;
 

